/** Version stamp baked into this bundle by Vite at build time, in the
 *  shape `<pkg.version>+<git-short-sha>`. Compared against the live
 *  server's `/api/version` response and the `X-App-Version` header on
 *  every authed API call to detect a deploy. */
export const BUILT_VERSION: string = __APP_VERSION__;

// Tiny pub/sub for the most-recently-observed server version. The
// `api.json()` helper calls noteServerVersion() with the
// `X-App-Version` header on every API response, and the dedicated
// /api/version probe calls it too. The version-watcher hook subscribes
// to render the update banner. Centralising it in this module avoids
// React component coupling - anything that touches the network can
// publish, and only one place needs to listen.
type VersionListener = (v: string | null) => void;
const versionListeners = new Set<VersionListener>();
let lastSeenServerVersion: string | null = null;

export function noteServerVersion(version: string): void {
  if (!version) return;
  if (version === lastSeenServerVersion) return;
  lastSeenServerVersion = version;
  for (const fn of versionListeners) {
    try {
      fn(version);
    } catch {
      // listener errors don't break dispatch
    }
  }
}

export function getLastServerVersion(): string | null {
  return lastSeenServerVersion;
}

export function subscribeServerVersion(fn: VersionListener): () => void {
  versionListeners.add(fn);
  // Replay the most recent value so a late subscriber doesn't have to
  // wait for the next API call to learn the current server version.
  if (lastSeenServerVersion) {
    try {
      fn(lastSeenServerVersion);
    } catch {
      // ignore
    }
  }
  return () => {
    versionListeners.delete(fn);
  };
}

interface ServerVersion {
  version: string;
  pkgVersion?: string;
  gitSha?: string;
  buildTime?: string;
  /** Severity tag for the current server version. Major releases
   *  warrant the interrupt-style banner; minor + patch advance the
   *  What's-new sparkle-dot count silently. null when the running
   *  daemon couldn't cross-reference its sha against the changelog
   *  (legacy or unknown). */
  kind?: "major" | "minor" | "patch" | null;
}

/** Probe the running server's version. Returns null on any failure
 *  (network, non-2xx, malformed JSON) - callers treat null as "no
 *  signal" and don't act. The endpoint is unauthenticated and
 *  cache-bypassed on the server, so this is safe to hit on a stale
 *  session and at sub-minute intervals. */
export async function fetchServerVersion(): Promise<ServerVersion | null> {
  try {
    const res = await fetch("/api/version", {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!res.ok) return null;
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return null;
    const body = (await res.json()) as Partial<ServerVersion>;
    if (typeof body?.version !== "string") return null;
    return body as ServerVersion;
  } catch {
    return null;
  }
}

/** Persisted dismissal target. When the user taps Refresh OR Dismiss
 *  the current serverVersion is stamped here. The mismatch check
 *  treats serverVersion === dismissedServerVersion as "already
 *  acknowledged" and suppresses the banner.
 *
 *  Why: the previous logic only suppressed for 60 s post-refresh, then
 *  re-fired indefinitely when the served Pages bundle was older than
 *  the daemon (e.g., a GH Actions deploy queued behind a daemon
 *  kickstart). Tapping Refresh would reload the same stale bundle, the
 *  cooldown would expire after 60 s, and the banner would re-fire
 *  forever. Pinning the dismissal to a specific serverVersion breaks
 *  that loop: the banner only re-appears when the daemon moves to a
 *  NEW version (a real subsequent deploy), not when the same mismatch
 *  re-evaluates. */
const DISMISSED_VERSION_KEY = "bv2:dismissedServerVersion";

export function rememberDismissedVersion(serverVersion: string | null | undefined): void {
  if (!serverVersion) return;
  try {
    localStorage.setItem(DISMISSED_VERSION_KEY, serverVersion);
  } catch {
    // localStorage may be unavailable; the in-memory check via the
    // version watcher's React state still holds for this session.
  }
}

function readDismissedVersion(): string | null {
  try {
    return localStorage.getItem(DISMISSED_VERSION_KEY);
  } catch {
    return null;
  }
}

/** True when the live server reports a different version than the bundle
 *  the page was loaded with AND the user hasn't already acknowledged
 *  this exact server version. The "+<sha>" tail is what catches
 *  commits that don't bump package.json (when polish-15's gitSha gating
 *  produces a new SHA for kind: major|minor commits). */
export function isVersionMismatch(serverVersion: string | null | undefined): boolean {
  if (!serverVersion || !BUILT_VERSION) return false;
  if (serverVersion === BUILT_VERSION) return false;
  const dismissed = readDismissedVersion();
  if (dismissed && dismissed === serverVersion) return false;
  return true;
}

/** Cooldown key in localStorage. When the user (or the auto-refresh
 *  countdown) triggers a refresh, we stamp Date.now() here BEFORE the
 *  reload. On the next page load, the version watcher checks this
 *  timestamp and suppresses the banner for REFRESH_COOLDOWN_MS even
 *  if the bake-vs-server comparison still mismatches.
 *
 *  Why: if the served bundle's bake somehow doesn't match the running
 *  server (which should never happen now that vite writes
 *  dist/version.json and the backend reads it, but defence in depth),
 *  the post-refresh page load would otherwise re-evaluate, see the
 *  same mismatch, and re-fire the banner - looping the user. The
 *  cooldown breaks that loop unconditionally. */
const REFRESH_COOLDOWN_KEY = "bv2:lastRefreshAt";
const REFRESH_COOLDOWN_MS = 60_000;

/** Layer B of the banner-noise reduction (polish-26): even after
 *  the server version advances to a new value, suppress the banner
 *  for 30 minutes after any prior banner showing was acknowledged
 *  (refresh OR dismiss). This caps "5 minor commits in 30 min = 5
 *  popups" by collapsing the cluster to one popup. The What's-new
 *  sparkle dot still increments per advance so the user can see all
 *  of them when they're ready.
 *
 *  Stored as a Date.now() timestamp keyed when the user dismissed.
 *  The timestamp ALSO updates when the user lands on the
 *  /settings?section=whatsnew panel since reading the changelog is
 *  an explicit acknowledgement of the deploys-since-last-visit. */
const BANNER_DEBOUNCE_KEY = "bv2:lastBannerAckAt";
const BANNER_DEBOUNCE_MS = 30 * 60 * 1000;

export function markBannerAcknowledged(): void {
  try {
    localStorage.setItem(BANNER_DEBOUNCE_KEY, Date.now().toString());
  } catch {
    /* localStorage unavailable; debounce is a backstop, not the
       primary defence */
  }
}

export function isInBannerDebounce(): boolean {
  try {
    const raw = localStorage.getItem(BANNER_DEBOUNCE_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    const elapsed = Date.now() - ts;
    return elapsed >= 0 && elapsed < BANNER_DEBOUNCE_MS;
  } catch {
    return false;
  }
}

export function markRefreshAttempt(): void {
  try {
    localStorage.setItem(REFRESH_COOLDOWN_KEY, Date.now().toString());
  } catch {
    // localStorage can throw in private mode / restricted contexts.
    // The cooldown is a backstop, not the primary defence - losing it
    // doesn't recreate the loop on its own.
  }
}

export function isInRefreshCooldown(): boolean {
  try {
    const raw = localStorage.getItem(REFRESH_COOLDOWN_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    const elapsed = Date.now() - ts;
    if (elapsed < 0 || elapsed > REFRESH_COOLDOWN_MS) return false;
    return true;
  } catch {
    return false;
  }
}

export function refreshCooldownRemainingMs(): number {
  try {
    const raw = localStorage.getItem(REFRESH_COOLDOWN_KEY);
    if (!raw) return 0;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return 0;
    return Math.max(0, REFRESH_COOLDOWN_MS - (Date.now() - ts));
  } catch {
    return 0;
  }
}

/** Hard reload that drops every layer of cache the PWA might have
 *  accumulated:
 *   - service-worker registrations (the precache that's caused every
 *     "you need to hard-refresh on your phone" handoff)
 *   - the Cache Storage API entries the workbox precache lives in
 *   - the page itself, with `location.reload()` (the bool argument is
 *     non-standard and ignored on iOS Safari, but the SW + cache wipe
 *     above is what actually forces fresh assets)
 *
 *  All steps are best-effort: a failure in one shouldn't block the
 *  others. The reload always runs, even if the SW / cache wipes
 *  silently throw, so the user is never left stuck in "Refreshing..." */
export async function applyForceRefresh(): Promise<void> {
  // Stamp the cooldown FIRST so the post-reload watcher has a
  // localStorage entry to read regardless of how the rest of the
  // wipe goes.
  markRefreshAttempt();
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch {
    // ignore
  }
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch {
    // ignore
  }
  // Cache-bust query string so the SPA shell itself bypasses any
  // intermediate proxy / browser disk cache that doesn't honour the
  // server's no-store header (looking at you, iOS Safari PWA shell).
  const url = new URL(window.location.href);
  url.searchParams.set("v", Date.now().toString(36));
  window.location.replace(url.toString());
}
